import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import { GIGS_COPY } from "../copy";
import type { LocalSelection } from "../types";

interface YesNoPillProps {
  /** The effective value to show as selected — a local edit if present, else the server value. */
  value: LocalSelection;
  disabled?: boolean;
  onChange: (value: "YES" | "NO") => void;
  testID?: string;
}

/**
 * Segmented Yes/No pill toggle. Visual/structural precedent:
 * `src/screens/Returns/components/BottomSheet.tsx`'s `tabButton` styling
 * (solid-black selected / white-outline unselected, borderRadius: 100).
 * A day whose real selection is UNSET shows neither pill as selected.
 */
const YesNoPill: React.FC<YesNoPillProps> = ({
  value,
  disabled = false,
  onChange,
  testID,
}) => {
  const isYes = value === "YES";
  const isNo = value === "NO";

  return (
    <View style={styles.container} testID={testID}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Mark yes"
        accessibilityState={{ selected: isYes, disabled }}
        disabled={disabled}
        onPress={() => onChange("YES")}
        style={[
          styles.pill,
          styles.yesPill,
          isYes && styles.yesPillSelected,
          disabled && styles.pillDisabled,
        ]}
      >
        <Text
          variant="body_text_3"
          style={[styles.pillText, isYes && styles.pillTextSelected]}
        >
          {GIGS_COPY.marking.yes}
        </Text>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Mark no"
        accessibilityState={{ selected: isNo, disabled }}
        disabled={disabled}
        onPress={() => onChange("NO")}
        style={[
          styles.pill,
          styles.noPill,
          isNo && styles.noPillSelected,
          disabled && styles.pillDisabled,
        ]}
      >
        <Text
          variant="body_text_3"
          style={[styles.pillText, isNo && styles.pillTextSelected]}
        >
          {GIGS_COPY.marking.no}
        </Text>
      </Pressable>
    </View>
  );
};

export default YesNoPill;

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    gap: 8,
  },
  pill: {
    minWidth: 56,
    height: 34,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: color.lightGrey,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 14,
  },
  yesPill: {
    backgroundColor: color.white,
  },
  yesPillSelected: {
    backgroundColor: color.gigsOnDutyGreen,
    borderColor: color.gigsOnDutyGreen,
  },
  noPill: {
    backgroundColor: color.white,
  },
  noPillSelected: {
    backgroundColor: color.black,
    borderColor: color.black,
  },
  pillDisabled: {
    opacity: 0.5,
  },
  pillText: {
    color: color.mediumBlack,
  },
  pillTextSelected: {
    color: color.white,
  },
});
